// src/pages/Marketing/components/BusinessTypeSelect.js
//
// A searchable combobox standing in for a native <select> on DemoRequestForm's
// own "business type" field. Two reasons this exists instead of a plain
// <select>: (1) a native <select> always renders visible option text inside
// its own box, even the disabled placeholder option, so it can never look
// "genuinely empty" the way MarketingApp.css's floating-label rules expect —
// the label and the option text collided at rest; (2) the user separately
// asked for the list to be searchable. A real <input type="text"> IS
// genuinely empty until a value is chosen, so it works correctly with the
// existing .mkt-field floating-label CSS with no special-casing, and typing
// into it filters the option list.
//
// `placeholder` is only ever passed to the underlying <input> while the
// dropdown is open (not at rest) — at rest, an empty closed field must stay
// visually empty for the floating label to sit centered the way an empty
// text input does; once focused/open the label has already floated up (via
// .mkt-field:focus-within), freeing the vertical center for the native
// placeholder hint to show while the visitor types.
//
// autoComplete is deliberately NOT the literal string "off" — Chrome ignores
// autocomplete="off" on fields it heuristically recognizes (matching it
// against the visible label text, here "Business type"/"نوع النشاط"), so an
// unrecognized-but-non-empty token is what actually suppresses its own
// suggestion popup; "off" itself would still let Chrome show one, which
// would visually stack on top of this component's own <ul> list below.
// mkt-combobox-arrow is a purely decorative chevron (pointer-events: none —
// clicks pass straight through to the input beneath it) standing in for the
// arrow a native <select> draws automatically, so the field still reads as
// "this opens a list" now that it's a plain text input.
import { useEffect, useRef, useState } from "react";

export default function BusinessTypeSelect({ value, onChange, options, placeholder, noMatchLabel, id }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const selected = options.find((o) => o.slug === value) || null;
  const displayValue = open ? query : selected ? selected.label || selected.slug : "";

  const filtered = query.trim()
    ? options.filter((o) => (o.label || o.slug).toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = (opt) => {
    onChange(opt.slug);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setOpen(true);
      setHighlight(0);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div className="mkt-combobox" ref={wrapRef}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="business-type-no-autofill"
        value={displayValue}
        placeholder={open ? placeholder : undefined}
        onFocus={() => {
          setOpen(true);
          setQuery("");
          setHighlight(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
      />
      <span className={`mkt-combobox-arrow${open ? " open" : ""}`} aria-hidden="true">
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      {open && (
        <ul className="mkt-combobox-list" role="listbox">
          {filtered.length === 0 ? (
            <li className="mkt-combobox-empty">{noMatchLabel}</li>
          ) : (
            filtered.map((opt, i) => (
              <li
                key={opt.slug}
                role="option"
                aria-selected={opt.slug === value}
                className={`mkt-combobox-option${i === highlight ? " highlighted" : ""}${opt.slug === value ? " selected" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(opt);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                {opt.label || opt.slug}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
